import { computeSchedule } from "./engine.js";
import { compareISO } from "./dates.js";
import {
  COMMITTED_STATUSES,
  STARTED_STATUSES,
  type CommittedJob,
  type EngineSettings,
  type ISODate,
  type JobStatus,
  type MachiningWindow,
  type SchedulableJob,
  type ScheduleResult,
} from "./types.js";

export function isCommittedStatus(status: JobStatus): boolean {
  return COMMITTED_STATUSES.includes(status);
}

export function hasStartedStatus(status: JobStatus): boolean {
  return STARTED_STATUSES.includes(status);
}

export function machiningWindows(result: ScheduleResult): MachiningWindow[] {
  return result.timeline
    .filter((s) => s.kind === "machining")
    .map((s) => ({ start: s.start, end: s.end, hours: s.hours, allocations: s.allocations }));
}

/**
 * Deterministic order for committed jobs: latest due first, then id (decision D47).
 * The engine places each job backwards from its due date, so the job placed first takes the
 * latest free days. Going latest-due first leaves earlier-due jobs to run earlier.
 */
export function committedOrder(a: SchedulableJob, b: SchedulableJob): number {
  return compareISO(b.dueDate, a.dueDate) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export interface MultiScheduleResult {
  results: Map<string, ScheduleResult>;
  /** Machining windows of every committed job, as fed into the uncommitted jobs' schedules. */
  committed: CommittedJob[];
}

/**
 * Schedule a whole organisation's jobs consistently.
 *
 * Committed jobs (`scheduled`, `in_progress`) are scheduled latest-due first, each one
 * reducing the capacity available to the next. Uncommitted jobs (`quoted`, `at_post_process`,
 * `complete`) are then scheduled against the full committed load without adding to it.
 */
export function scheduleJobs(
  jobs: readonly SchedulableJob[],
  settings: EngineSettings,
  today: ISODate,
): MultiScheduleResult {
  const results = new Map<string, ScheduleResult>();
  const committed: CommittedJob[] = [];

  const committedJobs = jobs.filter((j) => isCommittedStatus(j.status)).sort(committedOrder);
  for (const job of committedJobs) {
    const result = computeSchedule({
      steps: job.steps,
      dueDate: job.dueDate,
      settings,
      committedJobs: committed,
      today,
      status: job.status,
    });
    results.set(job.id, result);
    committed.push({ id: job.id, machining: machiningWindows(result) });
  }

  for (const job of jobs) {
    if (results.has(job.id)) continue;
    results.set(
      job.id,
      computeSchedule({
        steps: job.steps,
        dueDate: job.dueDate,
        settings,
        committedJobs: committed,
        today,
        status: job.status,
      }),
    );
  }

  return { results, committed };
}
